import axios from 'axios';
import { pushEvent, EVENTS } from './dataLayer';

const API_URL = process.env.REACT_APP_API_URL;
// /audio-click is served by the Audify server ({{audify_server}} in Postman). It shares the
// dastaan host today, so this only needs overriding if the two are split apart later.
const CLICK_API_URL = process.env.REACT_APP_AUDIFY_API_URL || API_URL;

// Console tracing for the consumption APIs. Flip to false to silence it.
const TRACKING_DEBUG = true;

// Fixed vocabulary for the `section` field of /audio-click, so analytics only ever
// sees these values. Add a constant here rather than passing a literal at the call site.
export const SECTIONS = {
  HOME_SLIDER: 'home_slider',
  TOP_CONTENT: 'top_content',
  HOME_CATEGORY: 'home_category',
  SEARCH: 'search',
  CATEGORY_DETAIL: 'category_detail',
  EPISODE_PLAY: 'episode_play',
};

const getToken = () => localStorage.getItem('dastaan__token');

const authHeaders = () => ({
  'Authorization': getToken(),
  'Accept': 'application/json',
  'Content-Type': 'application/json',
});

const trace = (...args) => {
  if (TRACKING_DEBUG) console.log('📊 [consumption]', ...args);
};

// Logs why a call never left the browser — usually a missing token or id, which is
// otherwise indistinguishable from the API silently failing.
const traceSkip = (endpoint, reason, data) => {
  if (TRACKING_DEBUG) console.warn(`⚠️ [consumption] ${endpoint} skipped — ${reason}`, data);
};

const traceError = (endpoint, error) => {
  if (TRACKING_DEBUG) {
    console.error(`❌ [consumption] ${endpoint} failed —`, error?.response?.status || '', error?.response?.data || error?.message || error);
  }
};

/**
 * Logs a click on a piece of content. Fire-and-forget: tracking must never
 * interrupt navigation or surface an error to the user.
 */
export const logAudioClick = (audio_file_id, section) => {
  // GTM gets the click for guests too, before the token check below.
  pushEvent(EVENTS.CONTENT_CLICK, { audio_file_id: audio_file_id ? String(audio_file_id) : null, section });
  if (!getToken()) return traceSkip('/audio-click', 'no auth token', { audio_file_id, section });
  if (!audio_file_id || !section) return traceSkip('/audio-click', 'missing audio_file_id or section', { audio_file_id, section });
  const data = {
    audio_file_id: parseInt(audio_file_id, 10),
    section,
  };
  trace('➡️ /audio-click', data);
  axios.post(`${CLICK_API_URL}/audio-click`, data, { headers: authHeaders() })
    .then((response) => trace('✅ /audio-click', data, response?.data))
    .catch((error) => traceError('/audio-click', error));
};

/**
 * Logs how far into an episode the user has listened. `duration` is the playback
 * position in seconds. Pass keepalive when firing during page unload, where an
 * ordinary XHR would be cancelled before it leaves the browser.
 */
export const logListenedTime = (episode_id, duration, { keepalive = false } = {}) => {
  const user_id = localStorage.getItem('dastaan__user_id');
  if (!getToken() || !user_id) return traceSkip('/add-listened-log', 'not logged in', { user_id, episode_id, duration });
  if (!episode_id || !(duration > 0)) return traceSkip('/add-listened-log', 'missing episode_id or nothing played yet', { episode_id, duration });
  const data = {
    user_id: parseInt(user_id, 10),
    episode_id: parseInt(episode_id, 10),
    duration: Math.round(duration),
  };
  if (keepalive) {
    trace('➡️ /add-listened-log (page unload)', data);
    fetch(`${API_URL}/add-listened-log`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
      keepalive: true,
    }).catch((error) => traceError('/add-listened-log', error));
    return;
  }
  trace('➡️ /add-listened-log', data);
  axios.post(`${API_URL}/add-listened-log`, data, { headers: authHeaders() })
    .then((response) => trace('✅ /add-listened-log', data, response?.data))
    .catch((error) => traceError('/add-listened-log', error));
};
